import Controller from '@ember/controller';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { all, task, dropTask } from 'ember-concurrency';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { showModal } from 'sh-www/utils/modal-controls';
import { APPLICATION } from 'sh-www/models/application';

/** @module */
class SettingsProfileController extends Controller {
  @service api;
  @service('state-managers/application') applicationStateManager;
  @service('commands/image-commands') imageCommandService;
  @service('commands/location-commands') locationCommandService;
  @service session;
  @service shData;
  @service store;
  @service('commands/user-commands') userCommandService;

  @task
  *checkIfDeletable() {
    const user = this.session.currentUser;

    if (user.isBuyer) {
      return true;
    }

    const userId = user.id;

    const applicationOptions = {
      params: {
        filter: {
          seller_id: userId,
          status: [
            APPLICATION.STATUS.ACTIVE,
            APPLICATION.STATUS.COMPLETE_PRODUCER,
            APPLICATION.STATUS.COMPLETE_PUBLISHER,
            APPLICATION.STATUS.CANCEL_PENDING,
          ].join(','),
        },
      },
    };

    const shPaymentOptions = {
      params: {
        filter: {
          user_id: userId,
          paid: 0,
        },
      },
    };

    try {
      const [openApplications, openShPayments] = yield all([
        this.api.request('applications', applicationOptions),
        this.api.request('sh-payments', shPaymentOptions)
      ]);

      return !openApplications.data.length && !openShPayments.data.length;
    } catch (error) {
      handleErrorResponseWithModal(this, error);
      return false;
    }
  }

  /**
   * @param {String} password
   */
  @dropTask
  *requestDeletion(password) {
    try {
      const user = yield this.userCommandService.requestDeletion(this.session.currentUser, password);
      this.set('model.user', user);
    } catch (error) {
      throw error;
    }
  }

  /**
   * @returns {string}
   */
  @action
  getDefaultUserPhotoUrl() {
    const {
      cloudVersion,
      cloudId,
    } = this.model.user.image;

    if (cloudVersion && cloudId) {
      return this.imageCommandService.cloudinaryProfileUrl(cloudVersion, cloudId);
    }

    return '';
  }

  /**
   * @param {DS.Model} photo
   * @returns {string}
   */
  @action
  getPhotoUrl(photo) {
    const { cloudVersion, cloudId } = photo;
    return this.imageCommandService.cloudinaryProfileUrl(cloudVersion, cloudId);
  }

  /**
   * @param {Object} param
   * @param {string} param.first_name
   * @param {string} param.middle_name
   * @param {string} param.last_name
   * @param {number} param.min_rate
   * @param {number} param.max_rate
   * @param {number} param.intercountry_travel
   * @param {string} param.phone
   * @param {string} param.slug
   * @param {DS.Model[]} param.services
   * @param {object|undefined} param.photo
   * @param {{ place_id: string }|undefined} param.place
   */
  @action
  async updateSeller({
    first_name: firstName,
    middle_name: middleName,
    last_name: lastName,
    min_rate: minRate,
    max_rate: maxRate,
    intercountry_travel: intercountryTravel,
    phone,
    slug,
    services,
    photo,
    place,
  }) {
    const {
      user,
      profile,
    } = this.model;

    const updatePromises = [
      this.locationCommandService.createLocationFromGooglePlace(place),
    ];

    if (photo) {
      updatePromises.push(this.imageCommandService.associateImage(photo, user));
    }

    updatePromises.push(this.userCommandService.updateSlug(user, slug));

    const [location] = await Promise.all(updatePromises);

    const userProps = { firstName, middleName, lastName, phone };
    const userRelationships = { services };

    if (location) {
      userRelationships.location = location;
    }

    const profileProps = {
      minRate: parseInt(minRate, 10),
      maxRate: parseInt(maxRate, 10),
      intercountryTravel,
    };

    const userSave = this.userCommandService.updateUser(user, userProps, userRelationships);
    const profileSave = this.userCommandService.updateUserProfile(user, profile, profileProps);

    await Promise.all([userSave, profileSave]);

    this.applicationStateManager.addToast('Settings updated');
  }

  /**
   * @param {Object} param
   * @param {string} param.first_name
   * @param {string} param.middle_name
   * @param {string} param.last_name
   * @param {string} param.description
   * @param {string} param.phone
   * @param {string} param.slug
   * @param {object|undefined} param.photo
   */
  @action
  async updateBuyer({
    first_name: firstName,
    middle_name: middleName,
    last_name: lastName,
    description,
    phone,
    slug,
    photo,
  }) {
    const { userCommandService, imageCommandService } = this;
    const { user } = this.model;

    const updatePromises = [
      userCommandService.updateUser(user, { firstName, middleName, lastName, description, phone }),
    ];

    if (slug !== user.slug) {
      updatePromises.push(userCommandService.updateSlug(user, slug));
    }

    if (photo) {
      updatePromises.push(imageCommandService.associateImage(photo, user));
    }

    await Promise.all(updatePromises);

    this.applicationStateManager.addToast('Settings updated');
  }

  /**
   * @param {File} file
   * @returns {Promise<DS.Model>}
   */
  @action
  uploadPhoto(file) {
    return this.imageCommandService.createDraftImage(file);
  }

  @action
  deleteAccount() {
    showModal(this, 'delete-account-modal', {
      requestDeletion: this.requestDeletion,
      checkIfDeletable: this.checkIfDeletable,
    });
  }
}

export default SettingsProfileController;
